import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import { FormattedMessage, useIntl } from 'dibs-react-intl';
import { getMeasurementUnit } from 'dibs-intl/exports/units';
import { Link } from 'dibs-elements/exports/Link.tsx';
import { Badge } from 'dibs-elements/exports/Badge.tsx';
import { PdpSharedSimilarSoldItemsDetailsCell } from './PdpSharedSimilarSoldItemsDetailsCell.tsx';
import { getDetailLabelAndValue } from './getDetailLabelAndValue.ts';
import { getSrcsetUrl } from 'dibs-image-utils/exports/srcSet.ts';
import { FormattedPrice } from 'dibs-buyer-price';
import { resolveSoldDate } from './resolveSoldDate.tsx';
import { useSimilarSoldItemsTracking } from '../hooks/useSimilarSoldItemsTracking.ts';

import styles from './PdpSharedSimilarSoldItemsColumn.module.scss';
import classnames from 'classnames';
import dibsCss from 'dibs-css';

import { type PdpSharedSimilarSoldItemsColumn_viewer$key } from './__generated__/PdpSharedSimilarSoldItemsColumn_viewer.graphql.ts';
import { type PdpSharedSimilarSoldItemsColumn_item$key } from './__generated__/PdpSharedSimilarSoldItemsColumn_item.graphql.ts';
import { type PdpSharedSimilarSoldItemsColumn_user$key } from './__generated__/PdpSharedSimilarSoldItemsColumn_user.graphql.ts';

const viewerFragment = graphql`
    fragment PdpSharedSimilarSoldItemsColumn_viewer on Viewer {
        ...useSimilarSoldItemsTracking_viewer
    }
`;

const itemFragment = graphql`
    fragment PdpSharedSimilarSoldItemsColumn_item on Item
    @argumentDefinitions(
        userId: { type: "String", defaultValue: "" }
        isTrade: { type: "Boolean", defaultValue: false }
        userCountryCode: { type: "String" }
        priceBookName: { type: "String" }
        catchNonFatalErrors: { type: "Boolean", defaultValue: false }
    ) {
        serviceId
        title
        vertical
        photoUrl: firstPhotoWebPath(size: master)
        localizedPdpUrl
        displayPrice(
            isTrade: $isTrade
            buyerId: $userId
            userCountryCode: $userCountryCode
            priceBookName: $priceBookName
            catchNonFatalErrors: $catchNonFatalErrors
            canShowOnHoldForDifferentBuyer: false
        ) {
            convertedAmountList {
                amount
                currency
            }
        }
        ...getDetailLabelAndValue_item
    }
`;

const userFragment = graphql`
    fragment PdpSharedSimilarSoldItemsColumn_user on User {
        preferences {
            measurementUnit
        }
    }
`;

const ItemImageAndTitle: FC<{
    imageUrl: string;
    title: string;
    isCurrentItem?: boolean;
    inModal?: boolean;
}> = ({ imageUrl, title, isCurrentItem, inModal }) => {
    return (
        <div
            className={classnames(dibsCss.relative, dibsCss.pxXsmall, dibsCss.pbXxsmall, {
                [dibsCss.bgWhite]: !inModal && isCurrentItem,
                [dibsCss.bgMoonstone]: inModal && isCurrentItem,
                [dibsCss.roundedT2px]: isCurrentItem,
            })}
        >
            <div
                className={classnames(
                    styles.wrapper,
                    dibsCss.flex,
                    dibsCss.justifyCenter,
                    dibsCss.roundedXxsmall,
                    dibsCss.mtXsmall
                )}
            >
                <div className={styles.itemImageDimensions}>
                    <img
                        className={classnames(
                            styles.itemImage,
                            styles.itemImageDimensions,
                            dibsCss.objectCover
                        )}
                        src={getSrcsetUrl(imageUrl, 200)}
                        alt={title}
                    />
                </div>
            </div>
            <div
                className={classnames(
                    dibsCss.sassyFontBodySmall,
                    dibsCss.textSatan,
                    dibsCss.mtXsmall,
                    dibsCss.lineClamp2
                )}
            >
                {title}
            </div>
            {isCurrentItem && (
                <div
                    className={classnames(dibsCss.absolute, {
                        [dibsCss.topXsmall]: inModal,
                        [dibsCss.rightXsmall]: inModal,
                        [styles.currentItemBadge]: !inModal,
                    })}
                >
                    <Badge type="pill" size="small-regular" backgroundColor="noir">
                        <FormattedMessage
                            id="abf.PdpRespSimilarSoldItemsColumn.currentItem"
                            defaultMessage="Current Item"
                        />
                    </Badge>
                </div>
            )}
        </div>
    );
};

export const PdpSharedSimilarSoldItemsColumn: FC<{
    viewer: PdpSharedSimilarSoldItemsColumn_viewer$key;
    item?: PdpSharedSimilarSoldItemsColumn_item$key | null;
    user?: PdpSharedSimilarSoldItemsColumn_user$key | null;
    itemSoldDate?: string | null;
    currency: string;
    isCurrentItem?: boolean;
    isLastItem?: boolean;
    inModal?: boolean;
    detailEntries: string[];
    isBelowMinPriceEstimate?: boolean;
    shouldShowEstimateValueBadge?: boolean;
}> = ({
    viewer: viewerRef,
    item: itemRef,
    user: userRef,
    itemSoldDate,
    currency,
    isCurrentItem,
    isLastItem,
    detailEntries,
    inModal,
    isBelowMinPriceEstimate,
    shouldShowEstimateValueBadge,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);
    const { locale, formatMessage } = useIntl();
    const { trackSimilarSoldItemsEcommerceClick } = useSimilarSoldItemsTracking({ viewer });

    const itemId = item?.serviceId;
    const priceAmount = item?.displayPrice?.[0]?.convertedAmountList?.find(
        convertedAmount => convertedAmount && convertedAmount.currency === currency
    )?.amount;

    const measurementUnit = user ? user.preferences?.measurementUnit : getMeasurementUnit(locale);

    const { photoUrl, title, localizedPdpUrl, vertical } = item || {};

    if (!photoUrl || !title || !localizedPdpUrl || !measurementUnit || !vertical || !itemId) {
        return null;
    }

    return (
        <>
            {isCurrentItem ? (
                <ItemImageAndTitle
                    imageUrl={photoUrl}
                    title={title}
                    inModal={inModal}
                    isCurrentItem
                />
            ) : (
                <Link
                    href={localizedPdpUrl}
                    target="_blank"
                    underline="none"
                    title={title}
                    onClick={() => {
                        trackSimilarSoldItemsEcommerceClick({ itemId });
                    }}
                >
                    <ItemImageAndTitle imageUrl={photoUrl} title={title} inModal={inModal} />
                </Link>
            )}
            <PdpSharedSimilarSoldItemsDetailsCell
                entry="PRICE"
                label={
                    isCurrentItem ? (
                        <FormattedMessage
                            id="abf.PdpRespSimilarSoldItemsColumn.currentPrice"
                            defaultMessage="Current Price"
                        />
                    ) : (
                        <FormattedMessage
                            id="abf.PdpRespSimilarSoldItemsColumn.soldPrice"
                            defaultMessage="Sold Price"
                        />
                    )
                }
                value={<FormattedPrice value={priceAmount?.toString() || ''} currency={currency} />}
                isCurrentItem={isCurrentItem}
                isLastItem={isLastItem}
                inModal={inModal}
                isBelowMinPriceEstimate={isBelowMinPriceEstimate}
                shouldShowEstimateValueBadge={shouldShowEstimateValueBadge}
            />
            <PdpSharedSimilarSoldItemsDetailsCell
                label={
                    <FormattedMessage
                        id="abf.PdpRespSimilarSoldItemsColumn.soldDate"
                        defaultMessage="Sold Date"
                    />
                }
                value={resolveSoldDate({ itemSoldDate, isCurrentItem })}
                isCurrentItem={isCurrentItem}
                isLastItem={isLastItem}
                inModal={inModal}
            />
            {detailEntries.map((entry, idx) => {
                const { label, value } = getDetailLabelAndValue({
                    item,
                    entry,
                    measurementUnit,
                    formatMessage,
                });
                return (
                    <PdpSharedSimilarSoldItemsDetailsCell
                        key={`detail-${idx}`}
                        label={label}
                        value={value}
                        isCurrentItem={isCurrentItem}
                        isLastItem={isLastItem}
                        isLastDetail={idx === detailEntries.length - 1}
                        entry={entry}
                        inModal={inModal}
                    />
                );
            })}
        </>
    );
};
